import QRCode from 'qrcode';

// renderA4Invoice — A4 tax invoice template for Red Sea Transport.
//
// Returns a Promise resolving to a complete HTML document string ready for
// window.print(). Replaces the thermal template on this install only.
//
// qrDataUrl can be either a data: URL (used as-is) or a plain text/URL
// (encoded to a PNG data URL via the `qrcode` library).
//
// Variants supported via opts.variant:
//   'original'    — a sale's first fiscal print ("Original Tax Invoice")
//   'copy'        — reprint before fiscalisation
//   'duplicate'   — reprint after fiscalisation
//   'creditNote'  — a credit note's first print
//   'cnDuplicate' — credit note reprint

const RSTRANSPORT_BANK = {
  accountName:   'ACCESS BANK',
  bankName:      'RED SEA IMPORT & EXPORT',
  accountNumber: '0252100720017',
  branchName:    'KABWE BRANCH',
  branchCode:    '',
  swiftCode:     '',
};

const h = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;','\'':'&#39;'}[c]));
const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (n) => Number(n || 0).toLocaleString();

const fmtDate = (d) => {
  if (!d) return '';
  const date = (d instanceof Date) ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (x) => String(x).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
};

const fmtDateTime = (d) => {
  if (!d) return '';
  const date = (d instanceof Date) ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (x) => String(x).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const titleFor = (variant) => {
  switch (variant) {
    case 'copy':        return 'TAX INVOICE — COPY';
    case 'duplicate':   return 'TAX INVOICE — DUPLICATE';
    case 'creditNote':  return 'CREDIT NOTE';
    case 'cnDuplicate': return 'CREDIT NOTE — DUPLICATE';
    case 'original':
    default:            return 'ORIGINAL TAX INVOICE';
  }
};

export async function renderA4Invoice({
  variant = 'original',
  business = {},
  order = {},
  items = [],
  customer = null,
  payment = {},
  curSym = 'K ',
  qrDataUrl = '',
  bank = RSTRANSPORT_BANK,
} = {}) {
  // Accept either an already-encoded data: URL (just use it) or a plain text
  // URL (encode here). ZRA returns a text URL, so this is what makes the
  // QR actually render on the printed A4 instead of a broken-image "QR".
  let qrImg = '';
  if (qrDataUrl) {
    if (typeof qrDataUrl === 'string' && qrDataUrl.startsWith('data:')) {
      qrImg = qrDataUrl;
    } else {
      try {
        qrImg = await QRCode.toDataURL(String(qrDataUrl), { width: 160, margin: 1 });
      } catch (_) { qrImg = ''; }
    }
  }
  const bName    = business.business_name    || 'Red Sea Transport';
  // Transport-specific fallbacks for address/phone when the business profile
  // doesn't carry them (user confirmed 2026-10-18). If the Red Sea Transport
  // office moves, update RSTRANSPORT_ADDRESS / RSTRANSPORT_PHONE below.
  const bPhone   = business.business_phone   || '0977 764 284';
  const bAddress = business.business_address || 'Sheki Sheki Road, Lusaka · P.O. Box 32010, Zambia';
  const bTpin    = business.zra_tpin         || '';
  const bSdc     = order.zra_sdc_id || business.zra_sdc_id || '';
  const bBhfId   = business.zra_bhf_id       || '';

  const localInvNo   = order.order_number || order.orderNumber || '';
  const cisInvcNo    = order.zra_cis_invc_no || '';
  const rcptNo       = order.zra_rcpt_no || '';
  // 2026-10-20 — Use ZRA's rcptNo (not Redsea's cisInvcNo) in the composite
  // receipt reference, matching the thermal receipt fix in SalesReport.js
  // (v1.13.148). cisInvcNo is Redsea's internal counter and drifts ahead of
  // rcptNo whenever a saveSales attempt fails or times out — the Transport
  // 896:404 run drifted the two numbers 8 apart, so the A4 header read
  // "INV<sdc>/60" while the fiscal footer and the ZRA portal both read /52.
  // If the sale was never signed, fall back to the local order_number.
  // CN variants also prefix "CRN" per T08A #13.
  const isCnVariant  = variant === 'creditNote' || variant === 'cnDuplicate';
  const prefix       = isCnVariant ? 'CRN' : 'INV';
  const sdcNumeric   = (order.zra_sdc_id || '').replace(/^SDC/i, '');
  const invoiceNo    = (sdcNumeric && rcptNo)
    ? `${prefix}${sdcNumeric}/${rcptNo}`
    : localInvNo;
  const vsdcTime     = order.zra_vsdc_rcpt_pbct_date || '';
  const intrlData    = order.zra_intrl_data || '';
  const signData     = order.zra_rcpt_sign || '';
  const dvcSrl       = business.zra_dvc_srl_no || '';
  const sdcId        = bSdc;
  const createdBy    = order.served_by || order.user_name || order.cashier_name || '';
  const salesDate    = order.created_at || order.date || new Date();

  // Deliver To block — customer-driven. User said BOX = email, address =
  // address. For walk-in customers both are blank.
  const cName    = customer?.name    || order.customer_name || 'Walk-in';
  const cBox     = customer?.email   || '';
  const cAddr    = customer?.address || '';
  const cTpin    = customer?.tpin    || order.buyer_tpin || '';
  const cTel     = customer?.phone   || '';

  // Items — expected shape: { product_name, quantity, unit_price, discount,
  // zra_vat_cat_snap, zra_vat_rate, zra_vat_taxbl_amt, zra_vat_amt, line_total }
  // Values can be computed on the fly if not pre-filled.
  const rowsHtml = items.map((it, i) => {
    const name   = it.product_name || it.name || '';
    const qty    = Number(it.quantity || 1);
    const rate   = Number(it.unit_price || it.price || 0);
    const disc   = Number(it.discount || 0);
    const vatCat = (it.zra_vat_cat_snap || it.vat_category || 'A').toUpperCase();
    const vatRt  = it.zra_vat_rate != null ? Number(it.zra_vat_rate) : (vatCat === 'A' || vatCat === 'B' ? 16 : 0);
    const totInc = qty * (rate - disc);
    const vatAmt = it.zra_vat_amt != null ? Number(it.zra_vat_amt) : (vatRt > 0 ? totInc - totInc / (1 + vatRt / 100) : 0);
    const vatEx  = it.zra_vat_taxbl_amt != null ? Number(it.zra_vat_taxbl_amt) : (totInc - vatAmt);
    const price  = rate;
    return `<tr>
      <td style="padding:4px 6px;border:1px solid #333;">${h(name)}</td>
      <td style="padding:4px 8px;border:1px solid #333;text-align:right;font-size:13px;font-weight:700;">${qty.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
      <td style="padding:4px 6px;border:1px solid #333;text-align:center;">${vatCat}</td>
      <td style="padding:4px 6px;border:1px solid #333;text-align:right;">${fmt(price)}</td>
      <td style="padding:4px 6px;border:1px solid #333;text-align:right;">${fmt(vatEx)}</td>
      <td style="padding:4px 6px;border:1px solid #333;text-align:right;">${fmt(disc)}</td>
      <td style="padding:4px 6px;border:1px solid #333;text-align:right;">${vatRt}%</td>
      <td style="padding:4px 6px;border:1px solid #333;text-align:right;">${fmt(vatAmt)}</td>
      <td style="padding:4px 6px;border:1px solid #333;text-align:right;">${fmt(totInc)}</td>
    </tr>`;
  }).join('');

  const subtotalExcl = items.reduce((s, it) => {
    const qty = Number(it.quantity || 1), rate = Number(it.unit_price || it.price || 0), disc = Number(it.discount || 0);
    const totInc = qty * (rate - disc);
    const vatRt = it.zra_vat_rate != null ? Number(it.zra_vat_rate) : 16;
    const vatAmt = it.zra_vat_amt != null ? Number(it.zra_vat_amt) : (vatRt > 0 ? totInc - totInc / (1 + vatRt / 100) : 0);
    return s + (totInc - vatAmt);
  }, 0);
  const totalVat = items.reduce((s, it) => {
    const qty = Number(it.quantity || 1), rate = Number(it.unit_price || it.price || 0), disc = Number(it.discount || 0);
    const totInc = qty * (rate - disc);
    const vatRt = it.zra_vat_rate != null ? Number(it.zra_vat_rate) : 16;
    const vatAmt = it.zra_vat_amt != null ? Number(it.zra_vat_amt) : (vatRt > 0 ? totInc - totInc / (1 + vatRt / 100) : 0);
    return s + vatAmt;
  }, 0);
  const totalIncl = subtotalExcl + totalVat;

  const paymentLabel = (() => {
    const c = Number(payment.cash || 0), m = Number(payment.momo || 0), b = Number(payment.bank || 0);
    if (c > 0 && m === 0 && b === 0) return 'Cash Sales';
    if (m > 0 && c === 0 && b === 0) return 'MoMo Sales';
    if (b > 0 && c === 0 && m === 0) return 'Bank Sales';
    if (c + m + b === 0) return 'Credit Sales';
    return 'Mixed Payment';
  })();

  const docNumber = ''; // deliberately blank per user instruction 2026-10-18

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>Invoice ${h(invoiceNo)}</title>
<style>
  @page { size: A4 portrait; margin: 15mm 14mm 18mm 14mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Times New Roman', Georgia, serif; font-size: 11px; color: #000; margin: 0;
         -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .wrap { width: 186mm; margin: 0 auto; padding-top: 2mm; display: flex; flex-direction: column; min-height: 260mm; }
  .main { flex: 0 0 auto; }
  .bottom-area { margin-top: auto; }
  .hdr-frame { border: 2px solid #000; padding: 12px 16px 0; margin-bottom: 10px;
               -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .hdr-frame .row { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; padding-bottom: 12px; }
  .hdr-frame .left { flex: 1; }
  .hdr-frame .co { font-size: 20px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; line-height: 1.1; }
  .hdr-frame .co-sub { font-size: 11.5px; margin-top: 5px; color: #222; line-height: 1.35; }
  .hdr-frame .right { text-align: right; font-size: 11px; min-width: 62mm; }
  .hdr-frame .right .k { color: #555; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; }
  .hdr-frame .right .v { font-family: 'Courier New', monospace; font-size: 11.5px; color: #000; margin-bottom: 5px; font-weight: 600; }
  .hdr-frame .title-strip { background: #f3f4f6 !important; color: #000 !important;
                            text-align: center; padding: 8px 0; margin: 0 -16px;
                            border-top: 1px solid #333; border-bottom: 1px solid #333;
                            font-size: 16px; font-weight: 700; letter-spacing: 10px;
                            -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .hdr-frame .title-strip .sub { display: inline-block; margin-left: 14px; font-size: 11px; letter-spacing: 4px; color: #555; font-weight: 500; }
  .meta { margin-top: 10px; display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
  .meta table { border-collapse: collapse; font-size: 11px; width: 100%; }
  .meta td { padding: 1px 4px; vertical-align: top; }
  .meta td.label { width: 42%; }
  .deliver-box { border: 1px solid #000; padding: 6px 8px; min-height: 70px; }
  .deliver-box .title { font-size: 10px; font-weight: 700; margin-bottom: 2px; }
  .items-table { width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 11px; }
  .items-table th { background: #f3f4f6; border: 1px solid #333; padding: 5px 6px; text-align: left; font-size: 10px; text-transform: uppercase; }
  .items-table th.num { text-align: right; }
  .items-table th.ctr { text-align: center; }
  .totals-row { display: flex; justify-content: space-between; margin-top: 12px; gap: 20px; }
  .totals-left { font-size: 11px; flex: 1; }
  .totals-right { font-size: 11px; width: 70mm; }
  .totals-right table { width: 100%; border-collapse: collapse; }
  .totals-right td { padding: 4px 6px; border: 1px solid #333; }
  .totals-right td.label { background: #f3f4f6; font-weight: 700; }
  .totals-right td.val { text-align: right; min-width: 30mm; }
  .totals-right tr.grand td { font-weight: 700; font-size: 12px; }
  .sig-row { margin-top: 18px; display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; font-size: 11px; }
  .sig-row .cell { }
  .sig-row .lbl { color: #333; margin-bottom: 24px; }
  .sig-row .line { border-top: 1px solid #000; padding-top: 2px; font-size: 10px; }
  .qr { text-align: center; }
  .qr img { width: 32mm; height: 32mm; display: block; margin: 0 auto; }
  .bank { margin-top: 16px; font-size: 11px; }
  .bank .title { font-weight: 700; margin-bottom: 4px; }
  .bank table { border-collapse: collapse; width: 100%; }
  .bank td { border: 1px solid #333; padding: 4px 8px; }
  .bank td.label { background: #f3f4f6; font-weight: 600; width: 20%; }
  .footer { margin-top: 10px; font-size: 9px; text-align: center; color: #333; }
  .fiscal-footer { margin-top: 10px; border-top: 1px dashed #666; padding-top: 6px; font-size: 9px; }
  .fiscal-footer table { width: 100%; border-collapse: collapse; }
  .fiscal-footer td { padding: 1px 4px; font-family: 'Courier New', monospace; }
  .fiscal-footer td.k { color: #333; width: 24%; }
  .reprint-band { text-align: center; font-size: 11px; font-weight: 700; border: 1px dashed #666; padding: 4px; margin: 8px 0; letter-spacing: 1px; }
</style>
</head>
<body>
<div class="wrap">
  <div class="main">
  <div class="hdr-frame">
    <div class="row">
      <div class="left">
        <div class="co">${h(bName)}${bName.toLowerCase().includes('ltd') || bName.toLowerCase().includes('limited') ? '' : ' (Z) LIMITED'}</div>
        ${bAddress ? `<div class="co-sub">${h(bAddress)}</div>` : ''}
        ${bPhone   ? `<div class="co-sub">Tel: ${h(bPhone)}</div>` : ''}
      </div>
      <div class="right">
        <div class="k">TPIN</div>
        <div class="v">${h(bTpin)}</div>
        <div class="k">Invoice No.</div>
        <div class="v">${h(invoiceNo)}</div>
        <div class="k">Date</div>
        <div class="v">${h(fmtDate(salesDate))}</div>
      </div>
    </div>
    <div class="title-strip">
      ${variant === 'creditNote' || variant === 'cnDuplicate' ? 'CREDIT NOTE' : (variant === 'original' ? 'ORIGINAL TAX INVOICE' : 'TAX INVOICE')}${variant === 'copy' ? '<span class="sub">COPY</span>' : ''}${variant === 'duplicate' ? '<span class="sub">DUPLICATE</span>' : ''}${variant === 'cnDuplicate' ? '<span class="sub">DUPLICATE</span>' : ''}
    </div>
    ${order.status === 'Reversed' && variant !== 'creditNote' && variant !== 'cnDuplicate' ? `
    <div style="margin-top:8px;padding:8px 14px;background:#fee2e2;border:2px solid #dc2626;border-radius:4px;text-align:center;font-weight:800;font-size:14px;color:#991b1b;letter-spacing:2px;">
      *** REVERSED ***<br>
      <span style="font-size:11px;font-weight:600;letter-spacing:0.5px;">This invoice has been voided — see the credit note</span>
    </div>` : ''}
  </div>

  <div class="meta">
    <table>
      <tr><td class="label">SDC ID:</td><td>${h(sdcId)}</td></tr>
      <tr><td class="label">Vsdc Receipt Date:</td><td>${h(vsdcTime)}</td></tr>
      <tr><td class="label">Security Data:</td><td style="font-family:monospace;font-size:10px;word-break:break-all;">${h(intrlData)}</td></tr>
      <tr><td class="label">Receipt Signature:</td><td style="font-family:monospace;font-size:10px;">${h(signData)}</td></tr>
      <tr><td class="label">Currency:</td><td>ZMW</td></tr>
      <tr><td class="label">Serial Number:</td><td>${h(rcptNo)}</td></tr>
    </table>
    <div>
      <div class="deliver-box">
        <div class="title">Deliver To:</div>
        <div>${h(cName)}</div>
        ${cBox  ? `<div>${h(cBox)}</div>`  : ''}
        ${cAddr ? `<div>${h(cAddr).replace(/\n/g, '<br/>')}</div>` : ''}
      </div>
      <div style="margin-top:6px;font-size:11px;">
        <div>Customer TPIN: ${h(cTpin || '0')}</div>
        <div>Customer Tel No: ${h(cTel)}</div>
      </div>
    </div>
  </div>


  <table class="items-table">
    <thead>
      <tr>
        <th style="width:26%;">Service / Product</th>
        <th class="num" style="width:12%;">Qty</th>
        <th class="ctr" style="width:5%;">Cat</th>
        <th class="num" style="width:8%;">Price</th>
        <th class="num" style="width:11%;">Ex Vat</th>
        <th class="num" style="width:6%;">Dis</th>
        <th class="ctr" style="width:6%;">Vat %</th>
        <th class="num" style="width:11%;">Vat Am.</th>
        <th class="num" style="width:15%;">Line Total</th>
      </tr>
    </thead>
    <tbody>
      ${rowsHtml}
    </tbody>
  </table>

  <div class="totals-row">
    <div class="totals-left"></div>
    <div class="totals-right">
      <table>
        <tr><td class="label">Sub Total Excluding Tax</td><td class="val">${fmt(subtotalExcl)}</td></tr>
        <tr><td class="label">VAT 16%</td><td class="val">${fmt(totalVat)}</td></tr>
        <tr class="grand"><td class="label">Total Invoice Value</td><td class="val">ZMW ${fmt(totalIncl)}</td></tr>
      </table>
    </div>
  </div>

  </div><!-- /.main -->

  <div class="bottom-area">
    <div class="sig-row">
      <div class="cell"><div class="lbl">Customer Sign:</div><div class="line">&nbsp;</div></div>
      <div class="cell"><div class="lbl">Created By:</div><div class="line">${h(createdBy) || '&nbsp;'}</div></div>
      <div class="cell qr">
        ${qrImg ? `<img src="${qrImg}" alt="QR"/>` : '<div style="font-size:9px;color:#777;">(QR not available)</div>'}
      </div>
    </div>
    ${(order.notes || order.remark) ? `
    <div style="margin-top:10px;padding:8px 12px;border:1px solid #bbb;background:#fafafa;font-size:10px;">
      <strong>Notes / Reference:</strong> ${h(order.notes || order.remark)}
    </div>` : ''}

    <div class="bank">
      <div class="title">Bank Details</div>
      <table>
        <tr>
          <td class="label">Account Name</td><td>${h(bank.accountName)}</td>
          <td class="label">Branch Name</td><td>${h(bank.branchName)}</td>
        </tr>
        <tr>
          <td class="label">Bank Name</td><td>${h(bank.bankName)}</td>
          <td class="label">Branch Code</td><td>${h(bank.branchCode)}</td>
        </tr>
        <tr>
          <td class="label">Account Number</td><td>${h(bank.accountNumber)}</td>
          <td class="label">Swift Code</td><td>${h(bank.swiftCode)}</td>
        </tr>
      </table>
    </div>
  </div><!-- /.bottom-area -->

</div>
</body>
</html>`;
}

export default renderA4Invoice;
